import { CSSProperties, FC, PropsWithChildren, ReactNode, forwardRef, useEffect } from 'react'
import classNames from 'classnames'

import {
  HorizontalScroll as ScrollProps,
  useHorizontalScroll,
} from '../../hooks/useHorizontalScroll'

import styles from './HorizontalScroll.module.scss'

interface HorizontalScrollProps {
  className?: string
  innerClassName?: string
  containerClassName?: string
  fadeWidth?: string
  shouldRestorePosition?: boolean
  renderAdditionalContent?: (scrollProps: ScrollProps) => ReactNode
  onMouseOver?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void
}

export const HorizontalScroll = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HorizontalScrollProps>
>((props, ref) => {
  const {
    className,
    children,
    innerClassName,
    containerClassName,
    fadeWidth = '7rem',
    shouldRestorePosition,
    renderAdditionalContent,
    onMouseOver,
    onMouseLeave,
  } = props

  const scrollProps = useHorizontalScroll<HTMLDivElement>({
    shouldRestorePosition,
  })

  const {
    scrollContainerRef,
    scrollContainerInnerRef,
    hasScrolledToEnd,
    hasScrolledToStart,
    isScrollable,
  } = scrollProps

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) {
      return
    }

    const resetScrollPosition = () => {
      if (scrollContainer.scrollLeft !== 0) {
        scrollContainer.scrollLeft = 0
      }
    }

    resetScrollPosition()

    const resizeObserver = new ResizeObserver(() => {
      resetScrollPosition()
    })

    resizeObserver.observe(scrollContainer)
    if (scrollContainerInnerRef.current) {
      resizeObserver.observe(scrollContainerInnerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [scrollContainerRef, scrollContainerInnerRef])

  return (
    <div
      className={classNames(styles.scrollContainerWrapper, className, {
        [styles.containerScrollable]: isScrollable,
      })}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
      ref={ref}
    >
      <div
        className={classNames(styles.scrollContainer, containerClassName)}
        ref={scrollContainerRef}
      >
        <div
          className={classNames(styles.scrollContainerInner, innerClassName)}
          ref={scrollContainerInnerRef}
        >
          {children}
        </div>
      </div>
      <HorizontalScrollFade.Left
        active={isScrollable && !hasScrolledToStart}
        style={{ width: fadeWidth }}
      />
      <HorizontalScrollFade.Right
        active={isScrollable && !hasScrolledToEnd}
        style={{ width: fadeWidth }}
      />
      {renderAdditionalContent?.(scrollProps)}
    </div>
  )
})

interface HorizontalScrollFadeProps {
  direction?: 'left' | 'right'
  style?: CSSProperties
  className?: string
  active?: boolean
}

export const HorizontalScrollFade: FC<HorizontalScrollFadeProps> & {
  Left: FC<Omit<HorizontalScrollFadeProps, 'direction'>>
  Right: FC<Omit<HorizontalScrollFadeProps, 'direction'>>
} = (props) => {
  const { direction = 'left', style, className, active } = props

  return (
    <div
      style={style}
      className={classNames(styles.scrollContainerFade, styles[direction], className, {
        [styles.active]: active,
      })}
    />
  )
}

HorizontalScrollFade.Left = (props: Omit<HorizontalScrollFadeProps, 'direction'>) => (
  <HorizontalScrollFade {...props} direction='left' />
)

HorizontalScrollFade.Right = (props: Omit<HorizontalScrollFadeProps, 'direction'>) => (
  <HorizontalScrollFade {...props} direction='right' />
)
